// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { xRegex } from "@awc/XDL/Utilities/validation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

const enum fields {
    emailRecipient = "emailRecipient",
    phoneNumber = "phoneNumber",
    contactUsLink = "contactUsLink",
    createAccountLink = "createAccountLink",
    faqLink = "faqLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipient]: string;
        [fields.phoneNumber]: string;
        [fields.contactUsLink]: LinkFieldValue;
        [fields.createAccountLink]: LinkFieldValue;
        [fields.faqLink]: LinkFieldValue;
    };
}

export interface HelpWidgetStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    phone?: TypographyPresentationProps;
    message?: TypographyPresentationProps;
    createAccountWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    faqWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    contactLink?: LinkPresentationProps;
}

export const helpWidgetStyles: HelpWidgetStyles = {};

type Props = OwnProps;
const styles = helpWidgetStyles;
const HelpWidget: FC<Props> = ({ fields }) => {
    const { url: contactUsUrl } = useGetLink(fields.contactUsLink);
    const { url: createAccountUrl } = useGetLink(fields.createAccountLink);
    const { url: faqUrl } = useGetLink(fields.faqLink);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title}>{translate("NEED HELP?")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.infoWrapper}>
                <Link aria-label={translate("Contact Us")} {...styles.contactLink} href={contactUsUrl}>
                    {translate("Contact Us")}
                </Link>
                <Typography {...styles.phone}>
                    {translate("CALL: ")}
                    <Link
                        aria-label={translate("Phone")}
                        href={`tel:${fields.phoneNumber}`}
                        target="_blank"
                        {...styles.contactLink}
                    >
                        {fields.phoneNumber}
                    </Link>
                </Typography>
                <Link aria-label={translate("CREATE AN ACCOUNT")} {...styles.link} href={createAccountUrl}>
                    {translate("CREATE AN ACCOUNT")}
                </Link>
                <Typography {...styles.message}>
                    {translate("Save time submitting RFQ and custom cable designs")}
                </Typography>
                <Link aria-label={translate("FAQs")} {...styles.link} href={faqUrl}>
                    {translate("FAQs")}
                </Link>
                <Typography {...styles.message}>
                    {translate("We can answer our most frequently asked questions")}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: HelpWidget,
    definition: {
        displayName: "HelpWidget",
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.emailRecipient,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "customerservice@awcwire.com",
                isRequired: true,
                regularExpression: xRegex.email,
            },
            {
                name: fields.phoneNumber,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "800-472-4655",
                isRequired: true,
                regularExpression: xRegex.phone,
            },
            {
                name: fields.contactUsLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.createAccountLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.faqLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
